import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { ArticleLabelType } from 'graphql-ts-types';
import type {
  AlignItemsProps,
  BackgroundColorProps,
  BorderColorProps,
  BorderRadiusProps,
  BorderStyleProps,
  BorderWidthProps,
  DisplayProps,
  HeightProps,
  JustifyContentProps,
  SpaceProps,
} from 'styled-system';
import {
  alignItems,
  backgroundColor,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  display,
  height,
  justifyContent,
  space,
} from 'styled-system';

import { GrowthbookFlag } from '../../constants/feature-flags';
import { useFeatureFlagIsOn } from '../../hooks/feature-flags';
import { useLocalization } from '../../providers/LocaleProvider';
import type { Color } from '../../theme/colors/types';
import type { ArticleLabelsList } from '../../types/labels';
import { filterDiscountLabels, getLabelStringValue } from '../../utils/labels';
import { BaseLabel, baseLabelStyles } from '../Label';
import { CaptionBoldCB1 } from '../Typography';

export interface SaleLabelProps {
  labels_v2?: ArticleLabelsList;
  regularPrice?: number;
  isV2?: boolean;
}

const baseSaleLabelStyles: Partial<ComponentProps<typeof BaseLabel>> = {
  ...baseLabelStyles,
  px: 'sp_4',
  mr: 'sp_4',
};

const SALE_LABEL_COLOR = 'sys.primary.text.default' as Color;

const SaleLabelBaseV2 = styled(CaptionBoldCB1)<
  BorderRadiusProps &
    BorderStyleProps &
    BorderWidthProps &
    BorderColorProps &
    DisplayProps &
    AlignItemsProps &
    HeightProps &
    BackgroundColorProps &
    JustifyContentProps &
    SpaceProps
>`
  ${borderRadius};
  ${borderStyle};
  ${borderWidth};
  ${borderColor};
  ${display};
  ${alignItems};
  ${justifyContent};
  ${height};
  ${backgroundColor};
  ${space};
`;

const saleLabelBaseV2Styles: ComponentProps<typeof SaleLabelBaseV2> = {
  bg: 'transparent' as Color,
  color: SALE_LABEL_COLOR,
  borderWidth: 'small',
  borderStyle: 'solid',
  borderColor: SALE_LABEL_COLOR,
  borderRadius: 'small',
  whiteSpace: 'nowrap',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  px: 'sp_4',
  mr: 'sp_4',
};

const SaleLabel = ({ labels_v2, regularPrice, isV2 = false }: SaleLabelProps) => {
  const { fc } = useLocalization();
  const withAbsoluteDiscount = useFeatureFlagIsOn(GrowthbookFlag.AbsoluteDiscount);

  if (!labels_v2 || !labels_v2.length) return null;

  // Filter to get only the sale label (percentage or absolute based on price)
  const filteredLabels = filterDiscountLabels(labels_v2, regularPrice, withAbsoluteDiscount);

  // Find the first sale label (should only be one based on filterDiscountLabels logic)
  const saleLabel = filteredLabels.find(
    label =>
      label &&
      (label.type === ArticleLabelType.PercentageDiscount ||
        label.type === ArticleLabelType.AbsoluteDiscount)
  );

  if (!saleLabel) return null;

  const stringValue = getLabelStringValue(saleLabel.value, saleLabel.type, fc);

  if (!stringValue) return null;

  if (isV2) {
    return (
      <SaleLabelBaseV2 data-testid="sale-label" aria-label={stringValue} {...saleLabelBaseV2Styles}>
        {stringValue}
      </SaleLabelBaseV2>
    );
  }

  return (
    <BaseLabel
      bg={'sys.marketing.orange' as Color}
      data-testid="sale-label"
      aria-label={stringValue}
      {...baseSaleLabelStyles}
    >
      {stringValue}
    </BaseLabel>
  );
};

export default SaleLabel;
